import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StepIndicator } from '../components/StepIndicator';
import { OtpInput } from '../components/OtpInput';
import '../styles/jobApplicationForm.scss';

const FALLBACK_CITIES = [
  
];

const EDUCATION_LEVELS = ['قسم علمي'];

const OTP_LENGTH = 4;
const RESEND_SECONDS = 300;
const MIN_AGE = 17;
const MAX_AGE = 23;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

function calcAge(dateStr) {
  const today = new Date();
  const dob = new Date(dateStr);
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

function validateFullName(value) {
  if (!value.trim()) return 'الاسم الكامل مطلوب';
  return '';
}

function validateNationalNumber(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'الرقم الوطني مطلوب';
  if (!/^\d{12}$/.test(trimmed)) return 'الرقم الوطني يجب أن يتكون من 12 رقماً';

  if (trimmed[0] !== '1') return 'الرقم الوطني غير صالح: يجب أن يبدأ بالرقم 1';
  if (trimmed[5] !== '0') return 'الرقم الوطني غير صالح: الرقم السادس يجب أن يكون 0';
  if (/^(\d)\1{5}$/.test(trimmed.slice(6))) {
    return 'الرقم الوطني غير صالح: لا يمكن أن تكون الأرقام الستة الأخيرة متطابقة';
  }

  const birthYear = parseInt(trimmed.slice(1, 5), 10);
  const age = new Date().getFullYear() - birthYear;
  if (age > MAX_AGE) return `عمرك بناءً على الرقم الوطني أكبر من الحد المسموح به (يجب أن يكون بين ${MIN_AGE} و ${MAX_AGE} سنة)`;
  if (age < MIN_AGE) return `عمرك بناءً على الرقم الوطني أقل من الحد المسموح به (يجب أن يكون بين ${MIN_AGE} و ${MAX_AGE} سنة)`;
  return '';
}

function validateBirthDate(value, nationalNumber) {
  if (!value) return 'تاريخ الميلاد مطلوب';

  const age = calcAge(value);
  if (age > MAX_AGE) return `عمرك أكبر من الحد المسموح للتقديم (يجب أن يكون بين ${MIN_AGE} و ${MAX_AGE} سنة)`;
  if (age < MIN_AGE) return `عمرك أقل من الحد المسموح للتقديم (يجب أن يكون بين ${MIN_AGE} و ${MAX_AGE} سنة)`;

  const trimmedNational = (nationalNumber || '').trim();
  if (/^\d{12}$/.test(trimmedNational)) {
    const nationalBirthYear = parseInt(trimmedNational.slice(1, 5), 10);
    const birthDateYear = new Date(value).getFullYear();
    if (nationalBirthYear !== birthDateYear) {
      return 'سنة الميلاد لا تتطابق مع سنة الميلاد المستخرجة من الرقم الوطني';
    }
  }
  return '';
}

function validatePhone(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'رقم الهاتف مطلوب';
  if (!/^09[1-5]\d{7}$/.test(trimmed)) return 'رقم الهاتف يجب أن يبدأ بـ 091 أو 092 أو 093 أو 094 أو 095 ويتكون من 10 أرقام';
  return '';
}

function validateCity(value) {
  if (!value) return 'يرجى اختيار المدينة';
  return '';
}

function validateEducationLevel(value) {
  if (!value) return 'يرجى اختيار المستوى الدراسي';
  return '';
}

function computeErrors(formData) {
  return {
    fullName: validateFullName(formData.fullName),
    nationalNumber: validateNationalNumber(formData.nationalNumber),
    phone: validatePhone(formData.phone),
    city: validateCity(formData.city),
    birthDate: validateBirthDate(formData.birthDate, formData.nationalNumber),
    educationLevel: validateEducationLevel(formData.educationLevel)
  };
}

export const JobApplicationForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    nationalNumber: '',
    phone: '',
    city: '',
    birthDate: '',
    educationLevel: ''
  });
  const [errors, setErrors] = useState({});
  const [cities, setCities] = useState(FALLBACK_CITIES);
  const [terms, setTerms] = useState('');
  const [confirmationCheckboxes, setConfirmationCheckboxes] = useState([]);
  const [checkedConfirmations, setCheckedConfirmations] = useState({});

  const [confirmChecked, setConfirmChecked] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isOtpReused, setIsOtpReused] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const timerRef = useRef(null);

  useEffect(() => {
    if (resendTimer <= 0) {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [resendTimer]);

  useEffect(() => {
    // Guard against landing here directly (e.g. a bookmarked link) while
    // registration is closed or the application limit has been reached.
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/website/content`);
        if (!res.ok) return;
        const json = await res.json();
        if (Array.isArray(json.cities) && json.cities.length > 0) {
          setCities(json.cities);
        }
        if (json.terms) setTerms(json.terms);
        if (Array.isArray(json.confirmationCheckboxes)) {
          setConfirmationCheckboxes(json.confirmationCheckboxes);
        }
        // Same exception as the intro page: existingApplicantsOnly lets
        // someone who already has a pending OTP reach the form even while
        // registration is closed — the backend still blocks a brand-new
        // number when they actually try to send/submit.
        if ((!json.registrationOpen && !json.existingApplicantsOnly) || json.isFull) {
          navigate('/', { replace: true });
        }
      } catch (err) {
        // If the check itself fails, let the user proceed — the server
        // enforces this authoritatively on send-otp/submit regardless.
      }
    };
    checkStatus();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'nationalNumber' ? value.replace(/\D/g, '').slice(0, 12) : value;

    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue };

      // As soon as the birth-year digits (positions 2-5) are typed, mirror
      // them into the date field so the applicant doesn't enter it twice —
      // keeps whatever month/day is already there, defaults to Jan 1st.
      if (name === 'nationalNumber' && nextValue.length >= 5) {
        const year = nextValue.slice(1, 5);
        const existing = prev.birthDate ? new Date(prev.birthDate) : null;
        const hasValidExisting = existing && !isNaN(existing);
        const month = hasValidExisting ? String(existing.getMonth() + 1).padStart(2, '0') : '01';
        const day = hasValidExisting ? String(existing.getDate()).padStart(2, '0') : '01';
        next.birthDate = `${year}-${month}-${day}`;
      }

      return next;
    });

    setErrors((prev) => {
      const next = { ...prev, [name]: '' };
      // birthDate's error can depend on nationalNumber (year cross-check) and vice-versa
      if (name === 'nationalNumber') next.birthDate = '';
      return next;
    });
  };

  const toggleConfirmation = (item) => {
    setCheckedConfirmations((prev) => ({ ...prev, [item]: !prev[item] }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    const fieldErrors = computeErrors(formData);
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
  };

  const allConfirmationsChecked = confirmationCheckboxes.every((item) => checkedConfirmations[item]);

  const isFormValid =
    Object.values(computeErrors(formData)).every((err) => !err) && confirmChecked && allConfirmationsChecked;

  const handleConfirmChange = (e) => {
    if (e.target.checked) {
      setShowTermsModal(true);
    } else {
      setConfirmChecked(false);
    }
  };

  const acceptTerms = () => {
    setConfirmChecked(true);
    setShowTermsModal(false);
  };

  const requestOtp = async (isResend = false) => {
    const res = await fetch(`${API_BASE_URL}/api/website/applications/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phoneNumber: formData.phone,
        nationalNumber: formData.nationalNumber,
        birthDate: formData.birthDate,
        isResend
      })
    });
    const json = await res.json();
    if (!res.ok) throw Error(json.message);
    return json;
  };

  const mapServerErrorToField = (message) => {
    if (!message) return 'submit';
    if (message.includes('تاريخ الميلاد')) return 'birthDate';
    if (message.includes('الرقم الوطني')) return 'nationalNumber';
    if (message.includes('الهاتف')) return 'phone';
    return 'submit';
  };

  const handleOpenOtpModal = async (e) => {
    e.preventDefault();
    const fieldErrors = computeErrors(formData);
    setErrors(fieldErrors);
    if (Object.values(fieldErrors).some(Boolean) || !confirmChecked || !allConfirmationsChecked) return;

    setIsSendingOtp(true);
    try {
      const result = await requestOtp();
      setOtp('');
      setOtpError('');
      setIsOtpReused(!!result?.alreadyPending);
      setResendTimer(RESEND_SECONDS);
      setShowOtpModal(true);
    } catch (err) {
      const field = mapServerErrorToField(err.message);
      setErrors((prev) => ({ ...prev, [field]: err.message || 'تعذر الاتصال بالخادم' }));
    }
    setIsSendingOtp(false);
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || isSendingOtp) return;
    setIsSendingOtp(true);
    setOtpError('');
    try {
      const result = await requestOtp(true);
      setOtp('');
      setIsOtpReused(!!result?.alreadyPending);
      setResendTimer(RESEND_SECONDS);
    } catch (err) {
      setOtpError(err.message || 'تعذر الاتصال بالخادم');
    }
    setIsSendingOtp(false);
  };

  const handleVerifyAndSubmit = async () => {
    if (otp.length !== OTP_LENGTH) {
      setOtpError('يرجى إدخال رمز التحقق كاملاً');
      return;
    }

    setIsVerifying(true);
    setOtpError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/website/applications/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          nationalNumber: formData.nationalNumber,
          phoneNumber: formData.phone,
          city: formData.city,
          birthDate: formData.birthDate,
          otpCode: otp
        })
      });
      const json = await res.json();

      if (!res.ok) {
        setOtpError(json.message);
        setIsVerifying(false);
        return;
      }

      navigate('/apply/success', {
        state: { referenceNumber: json.referenceNumber }
      });
    } catch (err) {
      setOtpError('تعذر الاتصال بالخادم');
      setIsVerifying(false);
    }
  };

  return (
    <div className="application-form-page">
      <div className="application-form-container">
        <StepIndicator currentStep={2} />

        <h1>بيانات المتقدم</h1>

        <form onSubmit={handleOpenOtpModal} noValidate>
          <div className={`input-group ${errors.fullName ? 'error' : ''}`}>
            <label htmlFor="fullName">الاسم الرباعي</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="أدخل اسمك الرباعي"
              autoComplete="name"
            />
            {errors.fullName && <span className="error-message">{errors.fullName}</span>}
          </div>

          <div className={`input-group ${errors.nationalNumber ? 'error' : ''}`}>
            <label htmlFor="nationalNumber">الرقم الوطني</label>
            <input
              type="text"
              id="nationalNumber"
              name="nationalNumber"
              inputMode="numeric"
              value={formData.nationalNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="أدخل الرقم الوطني المكون من 12 رقماً"
            />
            {errors.nationalNumber && (
              <span className="error-message">{errors.nationalNumber}</span>
            )}
          </div>

          <div className={`input-group ${errors.birthDate ? 'error' : ''}`}>
            <label htmlFor="birthDate">تاريخ الميلاد</label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.birthDate && <span className="error-message">{errors.birthDate}</span>}
          </div>

          <div className={`input-group ${errors.phone ? 'error' : ''}`}>
            <label htmlFor="phone">رقم الهاتف</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              dir="ltr"
              inputMode="numeric"
              maxLength={10}
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="09xxxxxxxx"
              autoComplete="tel"
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className={`input-group ${errors.city ? 'error' : ''}`}>
            <label htmlFor="city">المدينة</label>
            <select id="city" name="city" value={formData.city} onChange={handleChange} onBlur={handleBlur}>
              <option value="" disabled>
                اختر المدينة
              </option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
            {errors.city && <span className="error-message">{errors.city}</span>}
          </div>

          <div className={`input-group ${errors.educationLevel ? 'error' : ''}`}>
            <label htmlFor="educationLevel">المستوى الدراسي للشهادة الثانوية العامة</label>
            <select
              id="educationLevel"
              name="educationLevel"
              value={formData.educationLevel}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="" disabled>
                اختر المستوى الدراسي
              </option>
              {EDUCATION_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
            {errors.educationLevel && <span className="error-message">{errors.educationLevel}</span>}
          </div>

          {confirmationCheckboxes.map((item) => (
            <label key={item} className="confirm-checkbox">
              <input
                type="checkbox"
                checked={!!checkedConfirmations[item]}
                onChange={() => toggleConfirmation(item)}
              />
              <span>{item}</span>
            </label>
          ))}

          <label className="confirm-checkbox">
            <input type="checkbox" checked={confirmChecked} onChange={handleConfirmChange} />
            <span>أؤكد على صحة المعلومات المدخلة وأنني مستوفٍ لشروط القبول</span>
          </label>

          {errors.submit && (
            <div className="submit-error">
              <WarningIcon />
              <span>{errors.submit}</span>
            </div>
          )}

          <button type="submit" className="submit-btn" disabled={!isFormValid || isSendingOtp}>
            {isSendingOtp ? (
              <>
                <span className="loading-spinner"></span>
                جارٍ الإرسال...
              </>
            ) : (
              'إرسال الطلب'
            )}
          </button>
        </form>
      </div>

      {showTermsModal && (
        <div className="modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h2>شروط القبول</h2>
            <div className="modal-scroll">
              <p>{terms}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="confirm-btn" onClick={acceptTerms}>
                موافق
              </button>
              <button type="button" className="cancel-btn" onClick={() => setShowTermsModal(false)}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {showOtpModal && (
        <div className="modal-overlay">
          <div className="modal-box otp-modal">
            <h2>التحقق من رقم الهاتف</h2>
            <p className="otp-modal__phone">
              تم إرسال رمز التحقق إلى الرقم <bdi dir="ltr">{formData.phone}</bdi>
            </p>

            {isOtpReused && (
              <div className="otp-modal__warning">
                <WarningIcon />
                <span>
                  تم إدخال رقم الهاتف من قبل، يرجى إدخال آخر رمز تحقق حصلت عليه في المحاولة السابقة.
                </span>
              </div>
            )}

            <OtpInput length={OTP_LENGTH} value={otp} onChange={setOtp} disabled={isVerifying} />
            {otpError && <span className="error-message">{otpError}</span>}

            <div className="otp-resend">
              {resendTimer > 0 ? (
                <span>يمكنك إعادة إرسال الرمز بعد {Math.floor(resendTimer / 60)}:{String(resendTimer % 60).padStart(2, '0')} دقيقة</span>
              ) : (
                <button type="button" onClick={handleResendOtp} disabled={isSendingOtp}>
                  إعادة إرسال الرمز
                </button>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="confirm-btn"
                onClick={handleVerifyAndSubmit}
                disabled={otp.length !== OTP_LENGTH || isVerifying}
              >
                {isVerifying ? 'جارٍ الإرسال...' : 'تأكيد وإرسال الطلب'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplicationForm;
